import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn("[Sentry] No DSN configured — error tracking disabled");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // "production" | "development"
    release: `vetting-engine@${import.meta.env.VITE_APP_VERSION ?? "0.0.0"}`,

    // Performance monitoring
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,

    // Session replay for debugging UI issues (only in production)
    replaysSessionSampleRate: 0,       // Don't record all sessions
    replaysOnErrorSampleRate: 1.0,     // Record 100% of sessions with errors

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,    // Assessment text should be visible in replay
        blockAllMedia: false,
      }),
    ],

    // Don't send PII by default
    sendDefaultPii: false,

    // Filter noisy errors
    beforeSend(event) {
      // Skip ResizeObserver loop errors (benign browser noise)
      if (event.message?.includes("ResizeObserver loop")) return null;

      // Skip network errors from extensions
      if (event.message?.includes("chrome-extension://")) return null;

      return event;
    },

    // Tag critical paths for filtering
    beforeBreadcrumb(breadcrumb) {
      // Tag API calls to critical functions
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        const url = breadcrumb.data?.url ?? "";
        if (url.includes("generate-questions")) {
          breadcrumb.data = { ...breadcrumb.data, criticalPath: "question_generation" };
        } else if (url.includes("generate-report")) {
          breadcrumb.data = { ...breadcrumb.data, criticalPath: "report_generation" };
        } else if (url.includes("execute-code")) {
          breadcrumb.data = { ...breadcrumb.data, criticalPath: "code_execution" };
        } else if (url.includes("generate-assessment-link")) {
          breadcrumb.data = { ...breadcrumb.data, criticalPath: "session_creation" };
        }
      }
      return breadcrumb;
    },
  });

  console.log("[Sentry] Initialized for", import.meta.env.MODE);
}

/**
 * Set user context when a candidate starts a test
 */
export function setSentryUser(sessionId: string, email?: string) {
  Sentry.setUser({
    id: sessionId,
    email: email,
  });
}

/**
 * Clear user context after test completion
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Tag a session for easier filtering in Sentry
 */
export function setSentrySessionContext(sessionId: string, metadata?: Record<string, string>) {
  Sentry.setTag("session_id", sessionId);
  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      Sentry.setTag(key, value);
    });
  }
}

/**
 * Capture a custom error with context about which critical path failed
 */
export function captureApiError(
  error: Error,
  criticalPath: "question_generation" | "session_creation" | "code_execution" | "score_calculation" | "webhook_callback" | "proctoring",
  context?: Record<string, unknown>,
) {
  Sentry.withScope((scope) => {
    scope.setTag("critical_path", criticalPath);
    scope.setLevel("error");
    if (context) {
      scope.setContext("api_context", context);
    }
    Sentry.captureException(error);
  });
}
